import type {
  CashMovementKind,
  CostNature,
  ExpenseCategory,
  IngredientGroup,
  InvoiceStatus,
  MenuEngineeringClass,
  PayableStatus,
  PaymentMethod,
  PaymentType,
  RecipeKind,
  Unit,
  UnitSystem,
} from '@alexiarest/shared';
import {
  ALLERGEN_LABELS,
  ALLERGENS,
  BELOW_EBITDA_CATEGORIES,
  CATEGORY_ORDER,
  LABOR_CATEGORIES,
  MATERIAL_CATEGORIES,
  OPERATING_CATEGORIES,
  CATEGORY_LABELS,
  INGREDIENT_GROUP_LABELS,
  PAYMENT_METHOD_LABELS,
  PRESENTATION_UNITS,
  purchaseUnitsFor,
  PURCHASE_UNITS,
  UNITS,
} from '@alexiarest/shared';
import type { BadgeTone } from './components/Badge';

/**
 * Etiquetas de UI. Los calculos, el plan de cuentas y los grupos de
 * ingredientes son literales de las plantillas de Alex y viven en shared.
 */

export {
  ALLERGEN_LABELS,
  ALLERGENS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  INGREDIENT_GROUP_LABELS,
  PAYMENT_METHOD_LABELS,
};

export const INGREDIENT_GROUPS = Object.keys(INGREDIENT_GROUP_LABELS) as IngredientGroup[];
export const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

export const NATURE_LABELS: Record<CostNature, string> = {
  fijo: 'Fijo',
  variable: 'Variable',
};

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  contado: 'Contado',
  credito: 'Crédito',
};

export {
  DELIVERY_NOTE_LABEL,
  DELIVERY_NOTE_LABEL_LOWER,
  DELIVERY_NOTES_LABEL_LOWER,
} from '@alexiarest/shared';

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  facturado: 'Factura recibida',
  pendiente: 'Pendiente de factura',
  sin_factura: 'Sin factura',
};

export const PAYABLE_STATUS_LABELS: Record<PayableStatus, string> = {
  pagada: 'Pagada',
  por_vencer: 'Por vencer',
  vencida: 'Vencida',
};

/** Semaforo de las cuentas por pagar. */
export const PAYABLE_STATUS_TONE: Record<PayableStatus, BadgeTone> = {
  pagada: 'bien',
  por_vencer: 'atencion',
  vencida: 'mal',
};

export const CASH_MOVEMENT_LABELS: Record<CashMovementKind, string> = {
  ingresos_inversiones: 'Ingresos por inversiones',
  financiacion_aportes: 'Financiación y aportes',
  otros_ingresos: 'Otros ingresos',
  pago_impuestos: 'Pago de impuestos',
  otros_gastos: 'Otros gastos',
  ingresos_financieros: 'Ingresos financieros',
  gastos_financieros: 'Gastos financieros',
};

export const RECIPE_KIND_LABELS: Record<RecipeKind, string> = {
  plato: 'Plato',
  subreceta: 'Subreceta',
};

export const BLOCK_LABELS = {
  ingrediente: 'Ingredientes',
  empaque: 'Empaque',
} as const;

export const FUNDING_LABELS: Record<string, string> = {
  propio: 'Fondos propios',
  prestamo: 'Préstamo',
  leasing: 'Leasing / renting',
  subvencion: 'Subvención',
};

/**
 * Nombres visibles de los cuadrantes. "Caballo de batalla" sustituye a
 * "Vaca lechera" (ronda 3); el identificador interno sigue siendo `vaca`.
 */
export const MENU_CLASS_LABELS: Record<MenuEngineeringClass, string> = {
  estrella: 'Estrella',
  vaca: 'Caballo de batalla',
  puzzle: 'Puzzle',
  perro: 'Perro',
};

export const MENU_CLASS_HELP: Record<MenuEngineeringClass, string> = {
  estrella: 'Se vende mucho y deja un buen margen.',
  vaca: 'Se vende mucho, pero deja poco margen.',
  puzzle: 'Deja un buen margen, pero se vende poco.',
  perro: 'Se vende poco y deja poco margen.',
};

export const TAX_BASE_LABELS = {
  facturacion: 'Ventas del mes',
  nomina: 'Nómina del mes',
} as const;

export const ALL_UNITS = Object.keys(UNITS) as Unit[];

export function unitLabel(unit: Unit): string {
  return UNITS[unit]?.label ?? unit;
}

/** Opciones listas para un <select>. */
export function optionsFrom<T extends string>(
  values: T[],
  labels: Record<T, string>,
): Array<{ value: string; label: string }> {
  return values.map((value) => ({ value, label: labels[value] }));
}

/**
 * Unidades que se ofrecen al recetar. Peticion expresa de Alex: medidas
 * profesionales (g, kg, lb, oz, ml, cl, L, pz) — nada de tazas, cucharadas
 * ni docenas, que cada cocina interpreta como quiere. El motor las sigue
 * entendiendo por si llegan de datos importados, pero la UI no las ofrece.
 */
const PROFESSIONAL_UNITS = ALL_UNITS.filter(
  (unit) => !['taza', 'cucharada', 'cucharadita', 'docena'].includes(unit),
);

export const UNIT_OPTIONS = PROFESSIONAL_UNITS.map((unit) => ({
  value: unit,
  label: unitLabel(unit),
}));

/**
 * Unidades que se ENSEÑAN segun el sistema elegido en Ajustes (Alex, 4 sep
 * 2026): elegir "metrico" y seguir viendo lb y oz mezcladas con g y kg no
 * simplifica nada. Las de recuento (pz) y las de volumen valen en los dos
 * sistemas: no hay onzas liquidas ni tazas en el catalogo profesional, asi
 * que un restaurante imperial sigue midiendo el liquido en mL y L.
 *
 * El motor convierte igual entre todas; esto es solo lo que se ofrece
 * primero. Con «Ver más unidades» aparecen todas.
 */
const UNIDADES_DEL_SISTEMA: Record<UnitSystem, Unit[]> = {
  metrico: ['g', 'kg', 'ml', 'cl', 'l', 'pz'],
  imperial: ['oz', 'lb', 'ml', 'cl', 'l', 'pz'],
};

export function unitOptionsFor(system: UnitSystem | undefined, todas = false) {
  if (todas) return UNIT_OPTIONS;
  const propias = new Set(UNIDADES_DEL_SISTEMA[system ?? 'metrico']);
  return UNIT_OPTIONS.filter((o) => propias.has(o.value as Unit));
}

/** Lo mismo para los formatos de compra: la docena vale en los dos sistemas. */
export function purchaseUnitOptionsFor(system: UnitSystem | undefined, todas = false) {
  const opciones = purchaseUnitOptions(system);
  if (todas) return opciones;
  const propias = new Set<string>([...UNIDADES_DEL_SISTEMA[system ?? 'metrico'], 'docena']);
  return opciones.filter((o) => propias.has(o.value));
}

/**
 * Formatos de compra ordenados segun el sistema del restaurante: quien
 * trabaja en libras las ve primero, sin perder el resto de opciones.
 */
export function purchaseUnitOptions(system: UnitSystem | undefined) {
  return purchaseUnitsFor(system).map((unit) => ({
    value: unit,
    label: unitLabel(unit),
  }));
}

/** Lista por defecto (metrico), para pantallas sin ajustes cargados. */
export const PURCHASE_UNIT_OPTIONS = purchaseUnitOptions('metrico');

export const PRESENTATION_OPTIONS = PRESENTATION_UNITS.map((value) => ({
  value,
  label: value,
}));

/**
 * Los 3 grupos de la reunion con Alex, con las 22 categorias de sus
 * plantillas como subgrupos. El motor no cambia: es pura presentacion.
 */
export const CATEGORY_GROUPS: Array<{ label: string; categories: ExpenseCategory[] }> = [
  { label: 'Gastos de materia prima', categories: [...MATERIAL_CATEGORIES] },
  { label: 'Gastos de personal', categories: [...LABOR_CATEGORIES] },
  { label: 'Gastos generales', categories: [...OPERATING_CATEGORIES] },
  { label: 'Resultado financiero e impuestos', categories: [...BELOW_EBITDA_CATEGORIES] },
];

export const CATEGORY_OPTIONS = CATEGORY_ORDER.map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

export const GROUP_OPTIONS = INGREDIENT_GROUPS.map((value) => ({
  value,
  label: INGREDIENT_GROUP_LABELS[value],
}));

export function categoryLabel(category: ExpenseCategory): string {
  return CATEGORY_LABELS[category] ?? category;
}
